import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { View, ViewOpts, animate } from '@play-co/timestep-core/ui';
import { MagicLampsRewardIcon } from './MagicLampsRewardIcon';
import { magicLampsAnim } from './MagicLampsAnim';
import { MagicLampsRewardId } from 'src/replicant/match2/magiclamps/magiclamps.ruleset';

type IconPositions = Record<string, { x: number; y: number }>;

export class MagicLampsRewards extends View {
  private flexBox: FlexBoxView;
  private icons: Record<string, MagicLampsRewardIcon> = {};

  constructor(opts: ViewOpts) {
    super({
      width: 500,
      height: 180,
      ...opts,
    });

    this.flexBox = new FlexBoxView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      flex: {
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      },
      canHandleEvents: false,
    });
  }

  public addRewards(rewards: Record<MagicLampsRewardId, number>) {
    // Get current icon positions
    const fromPositions = this.getPositions();

    // Update existing or add new icons
    for (const [type] of Object.entries(rewards)) {
      this.addReward(type as MagicLampsRewardId);
    }

    // Force flexbox reder to update its content positions
    this.flexBox.render();

    // Get new icon positions
    const toPositions = this.getPositions();

    // Animate icons to new positions
    this.animatePositions(fromPositions, toPositions);
  }

  public updateRewards(rewards: Record<MagicLampsRewardId, number>) {
    for (const [type, value] of Object.entries(rewards)) {
      this.updateReward(type as MagicLampsRewardId, value);
    }
  }

  private async addReward(type: MagicLampsRewardId) {
    // Grand prize shows up in the middle of the popup
    if (type === 'grandPrize') return;

    if (!this.icons[type]) {
      this.icons[type] = new MagicLampsRewardIcon({
        superview: this.flexBox,
        opacity: 0,
      });
    }
  }

  private async updateReward(type: MagicLampsRewardId, value: number) {
    // Grand prize shows up in the middle of the popup
    if (type === 'grandPrize') return;

    // Just in case if icon does not exists
    if (!this.icons[type]) {
      this.addReward(type);
    }

    this.icons[type].updateOpts({ opacity: 1 });
    this.icons[type].setup({ type, value, scale: 0.65 });
  }

  private getPositions() {
    const positions: IconPositions = {};
    for (const [type, icon] of Object.entries(this.icons)) {
      positions[type] = { x: icon.style.x, y: icon.style.y };
    }
    return positions;
  }

  private animatePositions(from: IconPositions, to: IconPositions) {
    for (const [type, icon] of Object.entries(this.icons)) {
      const fromPos = from[type];
      const toPos = to[type];
      if (fromPos && toPos) {
        const diffX = fromPos.x - toPos.x;
        const diffY = fromPos.y - toPos.y;
        icon.style.offsetX = diffX;
        icon.style.offsetY = diffY;
        magicLampsAnim(
          icon,
          { offsetX: 0, offsetY: 0 },
          300,
          animate.easeInOutSine,
        );
      }
    }
  }

  public async greyOut() {
    await magicLampsAnim(this, { opacity: 0.5 }, 300);
  }

  public async fullColor() {
    await magicLampsAnim(this, { opacity: 1 }, 300);
  }

  public getIconByType(type: MagicLampsRewardId) {
    return this.icons[type];
  }
}
